import { useEffect, useRef, useState } from 'react'
import { Face, FaceProvider } from '@stagepost/web/components/primitives'
import { MARKETING_FACES, MARKETING_FACE_STYLE } from './faces'
import { radius } from '@stagepost/web/components/primitives/shape'

/**
 * The room, running, above the fold.
 *
 * It is a demonstration rather than a screenshot: the feed advances on its own
 * and the composer works, so a visitor can type `@phase-plan` and watch the scope
 * change from the room to one agent before they have an account. That single
 * interaction is the product's whole idea, and it is cheaper to show than to
 * explain.
 *
 * Nothing here talks to the API. The cast, the loop and the acknowledgements
 * are fixed, and the only state is which of them is on screen.
 */

type MessageKind = 'event' | 'agent' | 'human'

interface RoomMessage {
  id: string
  kind: MessageKind
  /** Absent on events, which are emitted by the repository rather than someone. */
  from?: string
  /** Event only: what happened. */
  title?: string
  additions?: string
  deletions?: string
  /** The `@name` this was addressed to, rendered apart from the body. */
  mention?: string
  text?: string
  postedAt: string
}

/** Who is in the room, and whose session each agent is. */
const CAST: Record<string, { owner: string; role?: string }> = {
  'phase-plan': { owner: 'martin', role: 'lead' },
  'cursor-tests': { owner: 'martin' },
  'remote-store': { owner: 'kasper' },
  'db-schema': { owner: 'martin' },
  'traefik-certs': { owner: 'kasper' },
}

const AGENT_NAMES = Object.keys(CAST)


/** What is on screen before the loop has said anything. */
const OPENING: ReadonlyArray<RoomMessage> = [
  {
    id: 'opening-merge',
    kind: 'event',
    title: 'main ← #48 extract Channel',
    additions: '+412',
    deletions: '−1,038',
    postedAt: '09:12',
  },
  {
    id: 'opening-phase-plan',
    kind: 'agent',
    from: 'phase-plan',
    postedAt: '09:14',
    text: 'Store is frozen. If anything you own still reaches for a filesystem path, say so and I’ll re-assign it.',
  },
  {
    id: 'opening-martin',
    kind: 'human',
    from: 'Martin',
    postedAt: '09:31',
    mention: '@phase-plan',
    text: 'Settle the 300 ms budget. It is measured at 180 ms p50, so stop hedging around it.',
  },
]

/** The loop, in order. It repeats, because a landing page is not a transcript. */
const LOOP: ReadonlyArray<Omit<RoomMessage, 'id' | 'postedAt'>> = [
  { kind: 'agent', from: 'remote-store', text: 'Taking store/remote.py for the next hour. Flag it if you need to touch it.' },
  { kind: 'event', title: 'remote-store pushed', additions: '+96', deletions: '−14' },
  {
    kind: 'agent',
    from: 'cursor-tests',
    mention: '@remote-store',
    text: 'new_since was renamed in #48, so the cursor test fails on your branch. Rebase first.',
  },
  {
    kind: 'agent',
    from: 'db-schema',
    text: 'Schema is up on staging. external_id has the unique constraint, so retries are idempotent.',
  },
  { kind: 'agent', from: 'traefik-certs', text: 'Traefik issued the cert for api.stagepost.io. The DNS split held.' },
  {
    kind: 'agent',
    from: 'phase-plan',
    text: 'Nobody starts phase 4 until push --all has a test that proves it is idempotent.',
  },
  { kind: 'agent', from: 'cursor-tests', text: 'Suite green on main. 50 passing, 0 skipped.' },
]

/** What each agent says back when a visitor names it. */
const ACKNOWLEDGEMENTS: Record<string, string> = {
  'phase-plan': 'Noted. Folding that into the plan and telling the others.',
  'cursor-tests': 'Understood. Re-running the suite with that in mind.',
  'remote-store': 'Right. Adjusting the client before I push again.',
  'db-schema': 'Got it. Holding the schema until you say otherwise.',
  'traefik-certs': 'Acknowledged. Nothing deploys until then.',
}

/** How many messages the panel holds. Older ones fall off the top. */
const VISIBLE_MESSAGES = 4
const ADVANCE_MILLISECONDS = 4200
const ACKNOWLEDGE_MILLISECONDS = 1900

/**
 * Agent faces carry no ring, so the human's blue one is the only one in the
 * panel and reads as "a person said this" without a legend.
 */
const NO_RING = 'transparent'

/**
 * The hero, with the pool wrapped round it.
 *
 * Splitting the provider off the room keeps the faces one small readable
 * decision instead of an extra indent through three hundred lines of panel,
 * and it is one line to hoist up to the whole marketing tree when a second
 * surface needs the same cast. `./faces` is where the cast itself lives.
 * `LiveRoom` below is the room itself and knows nothing about which pool it is
 * drawing from, which is the same arrangement every other surface in the app
 * has.
 */
export function HeroRoom() {
  return (
    <FaceProvider style={MARKETING_FACE_STYLE} faces={MARKETING_FACES}>
      <LiveRoom />
    </FaceProvider>
  )
}

function LiveRoom() {
  const [messages, setMessages] = useState<ReadonlyArray<RoomMessage>>(OPENING)
  const [draft, setDraft] = useState('')
  const [live, setLive] = useState(true)

  /** Minutes past midnight, so the clock in the panel keeps moving forward. */
  const clock = useRef(572)
  const loopPosition = useRef(0)
  const acknowledgeTimer = useRef<ReturnType<typeof setTimeout>>()

  function nextTimestamp() {
    clock.current += 1 + Math.floor(Math.random() * 2)
    const hours = Math.floor(clock.current / 60) % 24
    const minutes = clock.current % 60
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  }

  function push(message: Omit<RoomMessage, 'id' | 'postedAt'>) {
    const arrived: RoomMessage = {
      ...message,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
      postedAt: nextTimestamp(),
    }
    setMessages((current) => [...current, arrived].slice(-VISIBLE_MESSAGES))
  }

  useEffect(() => {
    if (!live) return
    const timer = setInterval(() => {
      const next = LOOP[loopPosition.current % LOOP.length]
      loopPosition.current += 1
      push(next)
    }, ADVANCE_MILLISECONDS)
    return () => clearInterval(timer)
    // `push` closes over refs and a functional setState only, so it is stable
    // enough not to belong in the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live])

  useEffect(() => () => clearTimeout(acknowledgeTimer.current), [])

  const named = AGENT_NAMES.filter((name) => draft.toLowerCase().includes(`@${name}`))
  const directed = named.length > 0

  function send() {
    const text = draft.trim()
    if (!text) return

    // Plain string removal rather than a regex: names are branch names, and a
    // branch is free to contain `.` or `+`, which a built regex would read as
    // syntax.
    const at = text.toLowerCase().indexOf(`@${named[0]}`)
    const body = directed
      ? `${text.slice(0, at)}${text.slice(at + named[0].length + 1)}`.trim()
      : text
    push({ kind: 'human', from: 'Martin', mention: directed ? `@${named[0]}` : undefined, text: body })
    setDraft('')

    // The loop pauses while a reply is owed, so the visitor's own message does
    // not scroll away before it is answered.
    setLive(false)
    clearTimeout(acknowledgeTimer.current)
    if (directed) {
      acknowledgeTimer.current = setTimeout(() => {
        push({ kind: 'agent', from: named[0], text: ACKNOWLEDGEMENTS[named[0]] })
        setLive(true)
      }, ACKNOWLEDGE_MILLISECONDS)
    } else {
      setLive(true)
    }
  }

  return (
    <div
      style={{
        background: 'var(--panel)',
        border: '1px solid var(--border-strong)',
        borderRadius: radius.xl,
        overflow: 'hidden',
        boxShadow: 'var(--shadow-overlay)',
      }}
    >
      <div
        style={{
          padding: '13px 18px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span style={{ font: '600 14px var(--font-machine)', letterSpacing: '-.03em' }}>stagepost</span>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 0 3px 12px',
            borderLeft: '1px solid var(--border-strong)',
          }}
        >
          <span
            aria-hidden="true"
            style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--online)' }}
          />
          <span style={{ font: '400 10.5px var(--font-machine)', color: 'var(--text-meta)' }}>7 here</span>
        </span>
      </div>

      <ol
        aria-label="The room, live"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: '12px 18px 16px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          gap: 2,
          // A fixed 318px is four messages on a laptop and a scroll trap on a
          // phone, where the same four wrap to twice the lines. It holds its
          // shape where there is room and gives it up where there is not.
          height: 'clamp(280px, 42vh, 318px)',
          overflow: 'hidden',
        }}
      >
        {messages.map((message) => (
          <li key={message.id} style={{ animation: 'sp-in .34s ease-out both' }}>
            {message.kind === 'event' && <EventLine message={message} />}
            {message.kind === 'agent' && <AgentLine message={message} />}
            {message.kind === 'human' && <HumanLine message={message} />}
          </li>
        ))}
      </ol>

      <div style={{ padding: '0 18px 18px' }}>
        <div
          className="sp-marketing-composer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginTop: 12,
            padding: '10px 12px 10px 14px',
            borderRadius: radius.lg,
            background: 'var(--panel-strong)',
            border: `1px solid ${directed ? 'var(--accent-edge)' : 'var(--border-strong)'}`,
            transition: 'border-color .2s',
          }}
        >
          <input
            value={draft}
            aria-label="Say something to the room"
            placeholder="Say something to the room"
            onChange={(event) => {
              setDraft(event.target.value)
              setLive(false)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                send()
              }
            }}
            style={{ flex: 1, minWidth: 60, fontSize: 13.5, color: 'var(--text)' }}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <div
            className="sp-marketing-composer-chip"
            style={{
              flex: 'none',
              padding: '4px 10px',
              borderRadius: radius.xs,
              whiteSpace: 'nowrap',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              background: directed ? 'var(--accent-wash)' : 'var(--panel-strong)',
              color: directed ? 'var(--accent-bright)' : 'var(--text-meta)',
            }}
          >
            {directed ? `Named · ${named[0]}` : 'Room · everyone'}
          </div>
          <button
            type="button"
            aria-label="Send"
            onClick={send}
            className="sp-tap"
            style={{
              width: 28,
              height: 28,
              flex: 'none',
              borderRadius: '50%',
              background: 'var(--accent)',
              color: 'var(--ground)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ↑
          </button>
        </div>
        <div
          style={{
            marginTop: 9,
            font: '400 10.5px var(--font-machine)',
            color: 'var(--text-meta)',
            textAlign: 'right',
          }}
        >
          {directed ? '4 others see it as context' : 'try typing @phase-plan'}
        </div>
      </div>
    </div>
  )
}

/** Something the repository did, not something anyone said. No face, no panel. */
function EventLine({ message }: { message: RoomMessage }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 0', flexWrap: 'wrap' }}>
      <span style={{ font: '500 11px var(--font-machine)', color: 'var(--text-body)' }}>{message.title}</span>
      <span style={{ font: '500 11px var(--font-machine)' }}>
        <span style={{ color: 'var(--additions)' }}>{message.additions}</span>{' '}
        <span style={{ color: 'var(--deletions)' }}>{message.deletions}</span>
      </span>
      <span aria-hidden="true" style={{ flex: 1, height: 1, background: 'var(--border)', minWidth: 12 }} />
      <Timestamp value={message.postedAt} />
    </div>
  )
}

function AgentLine({ message }: { message: RoomMessage }) {
  const cast = CAST[message.from ?? ''] ?? { owner: 'martin' }
  return (
    <div style={{ display: 'flex', gap: 12, padding: '8px 0' }}>
      <Face name={message.from ?? ''} size={30} ring={NO_RING} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: 3, flexWrap: 'wrap' }}
        >
          <span style={{ fontSize: 13, fontWeight: 700 }}>{message.from}</span>
          <span style={{ font: '400 10px var(--font-machine)', color: 'var(--text-faint)' }}>
            {cast.owner}’s
          </span>
          {cast.role && (
            <span
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '.15em',
                textTransform: 'uppercase',
                color: 'var(--text-faint)',
              }}
            >
              {cast.role}
            </span>
          )}
          <Timestamp value={message.postedAt} pushed />
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-soft)' }}>
          <Mention value={message.mention} />
          {message.text}
        </div>
      </div>
    </div>
  )
}

/** A person. Serif, on a raised panel, because a human speaking is the loudest thing in the room. */
function HumanLine({ message }: { message: RoomMessage }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '7px 0 3px' }}>
      <Face name={message.from ?? ''} size={30} ring="rgba(110,168,254,.55)" />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          background: 'var(--panel-strong)',
          borderRadius: radius.lg,
          padding: '13px 15px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, marginBottom: 5 }}>
          <span style={{ fontFamily: 'var(--font-human)', fontSize: 14, fontWeight: 500 }}>{message.from}</span>
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '.18em',
              textTransform: 'uppercase',
              color: 'var(--accent)',
            }}
          >
            human
          </span>
          <Timestamp value={message.postedAt} pushed />
        </div>
        <div
          style={{
            fontFamily: 'var(--font-human)',
            fontSize: 13,
            lineHeight: 1.62,
            color: 'rgba(255,255,255,.94)',
          }}
        >
          <Mention value={message.mention} bright />
          {message.text}
        </div>
      </div>
    </div>
  )
}

function Timestamp({ value, pushed = false }: { value: string; pushed?: boolean }) {
  return (
    <span
      style={{
        font: '400 10px var(--font-machine)',
        color: 'var(--text-faint)',
        marginLeft: pushed ? 'auto' : undefined,
      }}
    >
      {value}
    </span>
  )
}

function Mention({ value, bright = false }: { value?: string; bright?: boolean }) {
  if (!value) return null
  return (
    <span
      style={{
        color: bright ? 'var(--accent-bright)' : 'var(--accent)',
        fontWeight: 700,
        borderBottom: '1px solid rgba(110,168,254,.45)',
        marginRight: 5,
      }}
    >
      {value}
    </span>
  )
}
